import {
  Box,
  Grid,
  Link,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { green, grey, red } from "@mui/material/colors";
import { MetricsContext, TimeFrame } from "contexts/Metrics";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { CustomEvent } from "schemas/functions";

import { trackEvent } from "utils/tracking";

import { TimePeriod } from "../schema";

interface MetricProps {
  timeFrame: TimeFrame;
}

const styles = {
  tab: {
    fontSize: 12,
    color: grey[500],
    px: 1,
    py: 0,
    minHeight: "40px",
    height: "40px",
  },
  tabs: { minHeight: "40px", height: "40px" },
  tabsWrapper: {
    px: 2,
    py: 1,
  },
  metricsWrapper: {
    px: 3,
    py: 1,
  },
  descriptionText: {
    ml: 2,
    fontSize: 12,
  },
  metricText: {
    mb: 0.5,
  },

  arrowUp: {
    mr: 1,
    color: green[500],
  },
  arrowDown: {
    mr: 1,
    color: red[500],
  },
  subtext: {
    ml: 1,
    fontSize: 10,
    color: grey[500],
  },
};

const compareTimePeriod = (timePeriod: TimePeriod) => {
  if (timePeriod === TimePeriod.today) {
    return TimePeriod.yesterday;
  } else if (timePeriod === TimePeriod.thisMonth) {
    return TimePeriod.lastMonth;
  } else if (timePeriod === TimePeriod.thisWeek) {
    return TimePeriod.lastWeek;
  }
  return undefined;
};

const getArrowDescription = (timePeriod: TimePeriod) => {
  if (timePeriod === TimePeriod.today) {
    return `yesterday`;
  } else if (timePeriod === TimePeriod.thisWeek) {
    return `last week`;
  } else {
    return `last month`;
  }
};

function MetricText({
  compareDiff,
  countValue,
  description,
  showAsPercentage = false,
  timePeriod,
  showArrow = false,
  subtext = undefined,
}: {
  compareDiff: number | undefined;
  countValue: number | undefined;
  description: string;
  showAsPercentage?: boolean;
  timePeriod: TimePeriod;
  showArrow?: boolean;
  subtext?: JSX.Element;
}) {
  const displayArrow =
    showArrow && compareDiff !== undefined && compareDiff !== 0;

  return (
    <Box sx={styles.metricText}>
      {displayArrow && (
        <>
          {compareDiff > 0 ? (
            <Tooltip title={`Increase from ${getArrowDescription(timePeriod)}`}>
              <Box
                component="i"
                className="fa-solid fa-arrow-up"
                sx={styles.arrowUp}
              />
            </Tooltip>
          ) : (
            <Tooltip title={`Decrease from ${getArrowDescription(timePeriod)}`}>
              <Box
                component="i"
                className="fa-solid fa-arrow-down"
                sx={styles.arrowDown}
              />
            </Tooltip>
          )}
        </>
      )}
      <Typography component="span">
        {!showAsPercentage
          ? countValue
          : `${((countValue || 0) * 100).toFixed(1)}%`}
      </Typography>
      <Typography component="span" sx={styles.descriptionText}>
        {description}
        {subtext}
      </Typography>
    </Box>
  );
}

export default function Metric({ timeFrame }: MetricProps) {
  const { metrics, metricsLoading, fetchMetrics } = useContext(MetricsContext);
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);

  const [currentTimePeriod, setCurrentTimePeriod] = useState<TimePeriod>(
    timeFrame === TimeFrame.daily
      ? TimePeriod.today
      : timeFrame === TimeFrame.weekly
        ? TimePeriod.thisWeek
        : TimePeriod.thisMonth,
  );

  const handleChange = (event: CustomEvent, newValue: TimePeriod) => {
    event.stopPropagation();
    trackEvent("Stats Helper Tab Clicked", { tab: newValue });
    setCurrentTimePeriod(newValue);

    if ([TimePeriod.today].includes(newValue)) {
      fetchMetrics(newValue, true);
    } else if (metrics[newValue] === undefined) {
      fetchMetrics(newValue, true);
    }
  };

  const renderEmailsSentSubtext = (
    timePeriod: TimePeriod,
    outreachDiff?: number,
  ) => {
    if (outreachDiff && outreachDiff < 0) {
      return (
        <Box component="span" sx={styles.subtext}>
          <Box component="span" sx={{ color: red[500] }}>
            &#x25CF;
          </Box>{" "}
          {Math.abs(outreachDiff)} away from beating{" "}
          {getArrowDescription(timePeriod)}
        </Box>
      );
    } else {
      return <></>;
    }
  };

  useEffect(() => {
    setCurrentTimePeriod(
      timeFrame === TimeFrame.daily
        ? TimePeriod.today
        : timeFrame === TimeFrame.weekly
          ? TimePeriod.thisWeek
          : TimePeriod.thisMonth,
    );
  }, [timeFrame]);

  const currentPeriodMetrics = metrics[currentTimePeriod];

  const currentPeriodCompare = compareTimePeriod(currentTimePeriod);
  const currentPeriodCompareMetrics =
    currentPeriodCompare && metrics[currentPeriodCompare];

  const outreachDiff =
    currentPeriodCompareMetrics &&
    currentPeriodMetrics &&
    currentPeriodMetrics.outreachCount -
      currentPeriodCompareMetrics.outreachCount;

  return (
    <>
      <Grid item xs={12} sx={styles.tabsWrapper}>
        <Tabs
          sx={styles.tabs}
          value={currentTimePeriod}
          onChange={handleChange}
        >
          {timeFrame === TimeFrame.daily && (
            <Tab sx={styles.tab} value={TimePeriod.today} label="TODAY" />
          )}
          {timeFrame === TimeFrame.daily && (
            <Tab
              sx={styles.tab}
              value={TimePeriod.yesterday}
              label="YESTERDAY"
            />
          )}
          {timeFrame === TimeFrame.weekly && (
            <Tab
              sx={styles.tab}
              value={TimePeriod.thisWeek}
              label="THIS WEEK"
            />
          )}
          {timeFrame === TimeFrame.weekly && (
            <Tab
              sx={styles.tab}
              value={TimePeriod.lastWeek}
              label="LAST WEEK"
            />
          )}
          {timeFrame === TimeFrame.monthly && (
            <Tab
              sx={styles.tab}
              value={TimePeriod.thisMonth}
              label="THIS MONTH"
            />
          )}
          {timeFrame === TimeFrame.monthly && (
            <Tab
              sx={styles.tab}
              value={TimePeriod.lastMonth}
              label="LAST MONTH"
            />
          )}
        </Tabs>
      </Grid>
      {metricsLoading[currentTimePeriod] && (
        <Grid
          container
          justifyContent="center"
          rowGap={1}
          sx={styles.metricsWrapper}
        >
          {currentTimePeriod === TimePeriod.today ? (
            <>
              <Skeleton variant="rounded" height={21} width="100%" />
              <Skeleton variant="rounded" height={21} width="100%" />
              <Skeleton variant="rounded" height={21} width="100%" />
              <Skeleton variant="rounded" height={21} width="100%" />
            </>
          ) : currentTimePeriod === TimePeriod.yesterday ? (
            <>
              <Skeleton variant="rounded" height={21} width="100%" />
              <Skeleton variant="rounded" height={21} width="100%" />
              <Skeleton variant="rounded" height={21} width="100%" />
            </>
          ) : currentTimePeriod === TimePeriod.thisWeek ||
            currentTimePeriod === TimePeriod.thisMonth ? (
            <>
              <Skeleton variant="rounded" height={19} width="100%" />
              <Skeleton variant="rounded" height={19} width="100%" />
              <Skeleton variant="rounded" height={19} width="100%" />
              <Skeleton variant="rounded" height={19} width="100%" />
              <Skeleton variant="rounded" height={19} width="100%" />
              <Skeleton variant="rounded" height={19} width="100%" />
            </>
          ) : (
            <>
              <Skeleton variant="rounded" height={19} width="100%" />
              <Skeleton variant="rounded" height={19} width="100%" />
              <Skeleton variant="rounded" height={19} width="100%" />
              <Skeleton variant="rounded" height={19} width="100%" />
              <Skeleton variant="rounded" height={19} width="100%" />
            </>
          )}
        </Grid>
      )}
      {!metricsLoading[currentTimePeriod] && currentPeriodMetrics && (
        <Box sx={styles.metricsWrapper}>
          <MetricText
            compareDiff={outreachDiff}
            countValue={currentPeriodMetrics.outreachCount}
            description="Emails Sent"
            timePeriod={currentTimePeriod}
            showArrow={true}
            subtext={renderEmailsSentSubtext(currentTimePeriod, outreachDiff)}
          />
          {[
            TimePeriod.today,
            TimePeriod.thisWeek,
            TimePeriod.thisMonth,
          ]?.includes(currentTimePeriod) && (
            <MetricText
              compareDiff={
                currentPeriodCompareMetrics &&
                currentPeriodMetrics.scheduledCount -
                  currentPeriodCompareMetrics.scheduledCount
              }
              countValue={currentPeriodMetrics.scheduledCount}
              description="Emails Scheduled"
              timePeriod={currentTimePeriod}
            />
          )}
          <MetricText
            compareDiff={
              currentPeriodCompareMetrics &&
              currentPeriodMetrics.followUpCount -
                currentPeriodCompareMetrics.followUpCount
            }
            countValue={currentPeriodMetrics.followUpCount}
            description="Follow-ups Sent"
            timePeriod={currentTimePeriod}
          />
          <MetricText
            compareDiff={
              currentPeriodCompareMetrics &&
              currentPeriodMetrics.replyCount -
                currentPeriodCompareMetrics.replyCount
            }
            countValue={currentPeriodMetrics.replyCount}
            description="Replies Sent"
            timePeriod={currentTimePeriod}
          />
          {currentTimePeriod !== TimePeriod.today &&
            currentTimePeriod !== TimePeriod.yesterday && (
              <>
                <MetricText
                  compareDiff={
                    currentPeriodCompareMetrics &&
                    currentPeriodMetrics.respondedPercentage -
                      currentPeriodCompareMetrics.respondedPercentage
                  }
                  countValue={currentPeriodMetrics.respondedCount}
                  description={`Responses (${(
                    currentPeriodMetrics.respondedPercentage * 100
                  ).toFixed(1)}%)`}
                  timePeriod={currentTimePeriod}
                  subtext={
                    currentPeriodMetrics.respondedPercentage &&
                    currentPeriodMetrics.respondedPercentage * 100 > 10 ? (
                      <Box component="span" sx={styles.subtext}>
                        ⭐ Higher than your peers!
                      </Box>
                    ) : (
                      <></>
                    )
                  }
                />
                {subscription?.isSubscribed ? (
                  <MetricText
                    compareDiff={
                      currentPeriodCompareMetrics &&
                      currentPeriodMetrics.openedPercentage -
                        currentPeriodCompareMetrics.openedPercentage
                    }
                    countValue={currentPeriodMetrics.openedPercentage}
                    showAsPercentage
                    description={"Opened"}
                    timePeriod={currentTimePeriod}
                    subtext={
                      currentPeriodMetrics.openedPercentage &&
                      currentPeriodMetrics.openedPercentage * 100 > 50 ? (
                        <Box component="span" sx={styles.subtext}>
                          🙌 Above industry standard
                        </Box>
                      ) : (
                        <></>
                      )
                    }
                  />
                ) : (
                  <Box sx={{ fontSize: 13 }}>
                    <Link
                      sx={{ cursor: "pointer", fontWeight: 600 }}
                      onClick={() => setUpgradeDialogSource("Email Tracking")}
                    >
                      Upgrade
                    </Link>{" "}
                    to see your email opened rates
                  </Box>
                )}
              </>
            )}
        </Box>
      )}
    </>
  );
}
